import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, XCircle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType = 'info', duration: number = 5000) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const toast: Toast = { id, message, type, duration };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const styles =
    toast.type === 'success'
      ? 'bg-success-bg border-success/20 text-success'
      : toast.type === 'error'
        ? 'bg-danger-bg border-danger/20 text-danger'
        : toast.type === 'warning'
          ? 'bg-warning-bg border-warning/20 text-warning'
          : 'bg-primary/10 border-primary/20 text-primary';

  const Icon =
    toast.type === 'success'
      ? CheckCircle2
      : toast.type === 'error'
        ? XCircle
        : toast.type === 'warning'
          ? AlertTriangle
          : Info;

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-xl border shadow-card
        bg-background-card/80 backdrop-blur-xl
        animate-slide-in-right
        ${styles}
      `}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon className="h-5 w-5" />
      </div>
      <p className="flex-1 text-sm font-medium text-text-header">{toast.message}</p>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 p-1 rounded-md hover:bg-background-subtle/60 transition-colors text-text-muted hover:text-text-header"
        aria-label="Dismiss"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function showToast(message: string, type: ToastType = 'info', duration: number = 5000) {
  const container = document.getElementById('toast-root') || createToastRoot();

  const toastId = `toast-${Date.now()}`;
  const toast = document.createElement('div');
  toast.id = toastId;

  const styles =
    type === 'success'
      ? 'bg-success-bg border-success/20 text-success'
      : type === 'error'
        ? 'bg-danger-bg border-danger/20 text-danger'
        : type === 'warning'
          ? 'bg-warning-bg border-warning/20 text-warning'
          : 'bg-primary/10 border-primary/20 text-primary';

  toast.className = `
    flex items-start gap-3 p-4 rounded-xl border shadow-card mb-2 max-w-sm
    bg-background-card/80 backdrop-blur-xl
    animate-slide-in-right
    ${styles}
  `;

  toast.innerHTML = `
    <p class="flex-1 text-sm font-medium text-white">${message}</p>
    <button onclick="this.parentElement.remove()" class="flex-shrink-0 p-1 rounded-md hover:bg-[#1A1D1F]/60 transition-colors text-[#94969C]">
      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  `;

  container.appendChild(toast);

  if (duration > 0) {
    setTimeout(() => toast.remove(), duration);
  }
}

function createToastRoot() {
  const root = document.createElement('div');
  root.id = 'toast-root';
  root.className = 'fixed bottom-4 right-4 z-50 flex flex-col';
  document.body.appendChild(root);
  return root;
}
